<script setup lang="ts">
import { computed } from 'vue';
import { useModelRecordQuery } from '@/composables/api/useModelRecordQuery';
import { entityDisplayNameFor } from '@/composables/entityColumns';
import { useRecordHeading } from '@/composables/recordLayout/useRecordHeading';
import { hasBucketIconData, modelIconForRow } from '@/composables/modelIcons';
import { isPlaylistContainerEntity, readParentBucket } from '@/composables/searchInspectorRegistry';
import { useParentBucketMark } from '@/composables/useParentBucketMark';
import PlaylistContainerCounts from '@/components/DataTable/PlaylistContainerCounts.vue';
import PlaylistContainerZoneLine from '@/components/DataTable/PlaylistContainerZoneLine.vue';
import { readZoneDisplayString } from '@/utils/zoneDisplayString';
import PlaylistContainerSourceLine from '@/components/DataTable/PlaylistContainerSourceLine.vue';
import CustomListStatusIcon from '@/components/DataTable/CustomListStatusIcon.vue';
import { nameWithCustomListStatus } from '@/components/DataTable/customListStatus';
import { adminAppEntityFor, adminAppRecordUrl } from '@/utils/adminAppLink';

/**
 * Generic overlay-title for record inspectors: the record's top line and title
 * from the person's layout, as its own page shows them, once it's fetched
 * (shared query key with the inspector body — no extra request). While loading
 * the title is "<Noun> <id>". Entities with a bespoke header (e.g.
 * LocationZonePlayer) keep their own component via overlayHeaderComponentFor.
 *
 * The large variant keeps what only a peek has beside those rows: the model
 * icon, the ID link to the Admin app, and for a bucket or custom list the same
 * zone, name and source lines its search card draws, with the same counts.
 */
const props = withDefaults(
    defineProps<{
        entityId: string;
        recordId: number | string;
        /**
         * An already-loaded record (the cards split view has one in hand). When
         * given, the header reads from it instead of fetching.
         */
        row?: Record<string, unknown> | null;
        /** `bar` = slim title-bar text; `title` = the large heading above the section switcher. */
        size?: 'bar' | 'title';
    }>(),
    { row: null, size: 'bar' }
);

// A partial bucket row needs its genres before its icon can be resolved.
const recordQuery = useModelRecordQuery(
    () => props.entityId,
    () =>
        props.row && (props.entityId !== 'Bucket' || hasBucketIconData(props.row))
            ? null
            : props.recordId
);

const effectiveRow = computed(() =>
    props.row ? { ...recordQuery.data.value, ...props.row } : (recordQuery.data.value ?? null)
);

/**
 * The top line and the title, from the person's layout, the same two rows the
 * record's own page draws. Until the record loads, the title is "Account 55".
 */
const heading = useRecordHeading(() => props.entityId, () => props.recordId, effectiveRow);
const baseTitle = computed(() => heading.value.title);

/**
 * The bucket this container draws from — a custom list's source bucket, a zone
 * bucket's base bucket. Which one a record has changes what actually plays. The
 * large heading draws it on its own line through the source line the search
 * card uses, so the open record and the card it was opened from read the same.
 * The slim bar has one line, so it prints "name · bucket".
 *
 * Read through the registry's shared reader, which the card header uses too —
 * they had a copy each and the copies disagreed about the same row.
 *
 * Null until the record is actually in hand. The empty state ("No base
 * bucket") is a claim about the record, and claiming it from the entity type
 * alone put "Bucket #110412 · No base bucket" on screen while the record was
 * still loading — and left it there for good when the load failed.
 */
const parentBucket = computed(() =>
    effectiveRow.value === null ? null : readParentBucket(props.entityId, effectiveRow.value)
);

const parentBucketName = computed(() => parentBucket.value?.name ?? null);

/** The name, or the named empty state; null when this entity has no parent bucket at all. */
const parentBucketLabel = computed(
    () => parentBucket.value?.name ?? parentBucket.value?.missingLabel ?? null
);

// The slim bar's mark, resolved the way every surface resolves it: free from
// the nested reference when it carries a type, one request when it does not,
// and an alert either way, since a typeless reference is a data problem. The
// large heading's mark belongs to its source line.
const { icon: parentBucketIcon } = useParentBucketMark(() => (props.size === 'bar' ? parentBucket.value : null));

/**
 * The model's icon, so a record is recognisable before its name is read. Some
 * models draw differently per record — a master zone is not a plain zone — so
 * this resolves against the row, not just the entity.
 */
const icon = computed(() => modelIconForRow(props.entityId, effectiveRow.value));

/**
 * The record's id is also its address in the gvadmin desktop app, so the ID
 * label is the link out to it — the same `gvadmin://goto/…` link the id column
 * in the tables has always rendered. An entity we can't name (no entityId)
 * gets plain text rather than a link to nowhere.
 */
const adminAppHref = computed<string | null>(() => {
    const adminAppEntity = adminAppEntityFor(props.entityId);
    return adminAppEntity === '' ? null : adminAppRecordUrl(adminAppEntity, props.recordId);
});

const adminAppLinkTitle = computed(
    () => `Open ${entityDisplayNameFor(props.entityId)} ${props.recordId} in the Admin app`
);

// The name, for the large heading's hover text, because it clips. A custom
// list's status follows its name, as the icon beside the name says it.
const isCustomList = computed(() => props.entityId === 'LocationZoneCustomPlaylist');
const customListStatusId = computed(() => (isCustomList.value ? effectiveRow.value?.location_zone_custom_playlist_status_id : null));
const nameTitle = computed(() => nameWithCustomListStatus(baseTitle.value, customListStatusId.value));
// "name · bucket" for the slim title bar, which has one line to work with.
const titleText = computed(() =>
    parentBucketLabel.value === null ? nameTitle.value : `${nameTitle.value} · ${parentBucketLabel.value}`
);

/**
 * Only the two containers get the zone line under the heading; this component
 * is the heading for every other entity too, and their zone (when they have
 * one) stays in the Details fields where it always was.
 */
const showsZoneLine = computed(() => isPlaylistContainerEntity(props.entityId) && effectiveRow.value !== null);

// A vault has no zone. An empty first row would still take the row gap above the name, so a
// bucket with no zone moves its name and source line up a row, as on the card. A list keeps the
// first row, because its subscriber count sits there.
const hasZoneRow = computed(
    () => showsZoneLine.value && (isCustomList.value || readZoneDisplayString(effectiveRow.value?.location_zone) !== null)
);
</script>

<template>
    <!-- Large heading above the section switcher: the record's name on the left,
         its id on the right. The id is excluded from the field sections, so this
         is where it shows. -->
    <!-- For a bucket or custom list the heading is the card's grid: the zone line on
         row one, the name on row two, and the source line on row three, with the
         same subscriber and track counts at their right. -->
    <div v-if="size === 'title'" class="flex min-w-0 flex-col gap-1" data-testid="model-record-overlay-header">
    <!-- The top line, as the record's page draws it. An empty one removes the row.
         The first line of the header keeps clear of the buttons a host puts in its
         top-right corner. The record pane sets `--record-header-actions-width` to
         their width. Everywhere else it is unset and the margin is zero. -->
    <p
        v-if="heading.topLine"
        class="text-muted-foreground min-w-0 truncate text-sm mr-[var(--record-header-actions-width,0px)]"
        :title="heading.topLine"
        data-testid="record-peek-top-line"
    >
        {{ heading.topLine }}
    </p>
    <div
        class="min-w-0"
        :class="[
            showsZoneLine ? 'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-1 gap-y-1' : '',
            // With no top line, the title row is the first line, so it keeps clear of the buttons.
            heading.topLine ? '' : 'mr-[var(--record-header-actions-width,0px)]',
        ]"
    >
        <!-- The small right margin parts the name from a container's counts. Without
             counts the record's id ends flush with the header's right edge. -->
        <div
            class="col-start-1 flex min-w-0 items-baseline justify-between gap-2"
            :class="showsZoneLine ? (hasZoneRow ? 'row-start-2 mr-1' : 'row-start-1 mr-1') : 'row-start-1'"
            data-testid="model-record-overlay-name-row"
        >
            <h2
                class="flex min-w-0 flex-1 items-center gap-2 text-xl leading-tight font-semibold"
                :title="nameTitle"
            >
                <component
                    :is="icon"
                    v-if="icon"
                    class="size-5 shrink-0"
                    aria-hidden="true"
                    data-testid="model-record-overlay-icon"
                />
                <!-- A bucket or custom list name takes two lines and then clips, as on
                     its card. Every other record keeps its one clipped line. -->
                <span
                    class="min-w-0"
                    :class="showsZoneLine ? 'line-clamp-2' : 'truncate'"
                    data-testid="model-record-overlay-name"
                    >{{
                    baseTitle
                }}</span>
                <CustomListStatusIcon v-if="isCustomList" :status-id="customListStatusId" />
            </h2>
            <!-- The id doubles as the record's address in the Admin app. A bucket or custom list
                 leaves it out: its track count's tooltip already names the id. -->
            <a
                v-if="adminAppHref && !showsZoneLine"
                :href="adminAppHref"
                class="text-muted-foreground hover:text-primary focus-visible:text-primary shrink-0 font-mono text-sm tabular-nums whitespace-nowrap underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
                :title="adminAppLinkTitle"
                data-testid="model-record-overlay-admin-link"
            >
                ID {{ recordId }}
            </a>
            <span
                v-else-if="!showsZoneLine"
                class="text-muted-foreground shrink-0 font-mono text-sm tabular-nums whitespace-nowrap"
            >
                ID {{ recordId }}
            </span>
        </div>
        <template v-if="showsZoneLine">
            <PlaylistContainerZoneLine
                :row="effectiveRow!"
                :max-lines="2"
                class="col-start-1 row-start-1 mr-1 min-w-0 text-sm"
                data-testid="model-record-overlay-zone"
            />
            <!-- Clicking the track count switches the inspector to Tracks. -->
            <PlaylistContainerCounts :row="effectiveRow!" :entity-id="entityId" :name-row="hasZoneRow ? 2 : 1" />
            <PlaylistContainerSourceLine
                :row="effectiveRow!"
                :entity-id="entityId"
                class="col-span-3 col-start-1 text-sm"
                :class="hasZoneRow ? 'row-start-3' : 'row-start-2'"
            />
        </template>
    </div>
    </div>
    <h3
        v-else
        class="flex min-w-0 flex-1 items-center gap-1.5 truncate text-sm leading-6 font-semibold"
        :title="titleText"
        data-testid="model-record-overlay-header"
    >
        <component
            :is="icon"
            v-if="icon"
            class="size-4 shrink-0"
            aria-hidden="true"
            data-testid="model-record-overlay-icon"
        />
        <span class="min-w-0 truncate">{{ baseTitle }}</span>
        <template v-if="parentBucketLabel !== null">
            <span class="text-muted-foreground shrink-0" aria-hidden="true">·</span>
            <component
                :is="parentBucketIcon"
                v-if="parentBucketIcon"
                class="size-4 shrink-0"
                aria-hidden="true"
                data-testid="model-record-overlay-source-bucket-icon"
            />
            <span
                class="text-muted-foreground min-w-0 truncate font-normal"
                :class="parentBucketName === null ? 'italic' : ''"
                >{{ parentBucketLabel }}</span
            >
        </template>
    </h3>
</template>
